<template>
  <div>
    <div class="grid grid-cols-1 sm:grid-cols-3 gap-6">
      <!-- <StatCard label="จำนวนประชากรทั้งหมด" :value="totalPopulation" unit="คน" align="left">
        <template #icon>
          <div class="w-6 h-6">
            <PeopleIcon class="w-full h-full" />
          </div>
        </template>
      </StatCard> -->
      <StatCard label="เพศหญิง" :value="femaleCount" :unit="`คน (${femalePercentage} จาก ${totalPopulation} คน)`" align="left">
        <template #icon>
          <div class="w-6 h-6">
            <FemaleIcon class="w-full h-full text-pink-500" />
          </div>
        </template>
      </StatCard>
      <StatCard label="เพศชาย" :value="maleCount" :unit="`คน (${malePercentage} จาก ${totalPopulation} คน)`" align="left">
        <template #icon>
          <div class="w-6 h-6">
            <MaleIcon class="w-full h-full text-primary-1" />
          </div>
        </template>
      </StatCard>
      <StatCard label="อื่นๆ" :value="otherCount" :unit="`คน (${otherPercentage} จาก ${totalPopulation} คน)`" align="left">
        <template #icon>
          <div class="w-6 h-6">
            <AllGenderIcon class="w-full h-full text-primary-1" />
          </div>
        </template>
      </StatCard>
    </div>

    <div class="grid grid-cols-12 gap-6 mt-6">
      <div class="col-span-12 lg:col-span-4">
        <ChartCard title="ช่วงอายุ">
          <div class="h-[380px]">
            <DashboardBarEChart chart-id="pAgeRangeChart" type="bar" :data="ageRangeData" :options="genderOptions" fill-height />
          </div>
        </ChartCard>
      </div>
      <div class="col-span-12 lg:col-span-4">
        <ChartCard title="บัตรประชาชน">
          <div class="h-[400px]">
            <DashboardBarEChart chart-id="pIdCardChart" type="bar" :data="idCardData" :options="genderOptions" fill-height />
          </div>
        </ChartCard>
      </div>
      <div class="col-span-12 lg:col-span-4">
        <ChartCard title="สถานภาพสมรส">
          <div class="h-[420px]">
            <DashboardBarEChart chart-id="pMaritalChart" type="bar" :data="maritalData" :options="genderOptions" fill-height />
          </div>
        </ChartCard>
      </div>
    </div>

    <div class="flex items-center justify-between mt-10 mb-4 flex-wrap gap-3">
      <h2 class="text-xl font-medium text-primary-3">สรุปข้อมูลแยกตามช่วงวัย</h2>
      <PillTabs :model-value="ageTab" :tabs="ageTabs" @update:model-value="ageTab = $event" />
    </div>

    <AgeChildPanel v-if="ageTab === 'child'" :data="agePanelData.child" />
    <AgeStudentPanel v-else-if="ageTab === 'student'" :data="agePanelData.student" />
    <AgeWorkingPanel v-else-if="ageTab === 'working'" :data="agePanelData.working" />
    <AgeElderPanel v-else :data="agePanelData.elder" />
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";
import axios from "axios";
import StatCard from "../container/StatCard.vue";
import ChartCard from "../container/ChartCard.vue";
import PillTabs from "../container/PillTabs.vue";
import AgeChildPanel from "./person/AgeChildPanel.vue";
import AgeStudentPanel from "./person/AgeStudentPanel.vue";
import AgeWorkingPanel from "./person/AgeWorkingPanel.vue";
import AgeElderPanel from "./person/AgeElderPanel.vue";
import DashboardBarEChart from "../container/DashboardBarEChart.vue";
import { globalNumDecFixed } from "@/utils/format-number.js";

const store = useStore();
const filterParams = computed(() => store.getters["dashboardOverviewFilter/getFilterForApiWithAgeGroups"]);
const authHeaders = () => ({
  Authorization: `Bearer ${store.getters["userAuth/getToken"]}`,
  "Content-Type": "application/json",
});
const apiGet = (path, params = {}) =>
  axios.get(`${process.env.VUE_APP_BASE_API}${path}`, {
    headers: authHeaders(),
    params: { ...filterParams.value, ...params },
  }).catch((err) => err);

const P = { navy: "#3A506A", yellow: "#F4D35E", green: "#11D67A", blue: "#20A0D7", purple: "#AF52DE", gray: "#606468", red: "#F73C4B" };
const PALETTE = ["#3A506A", "#F4D35E", "#11D67A", "#20A0D7", "#AF52DE", "#606468", "#F73C4B", "#A9BCD0"];
const bar = (label, color, data, opts = {}) => ({
  label, backgroundColor: color, data, barPercentage: 0.6, categoryPercentage: 0.8, ...opts,
});

const barOptions = ({ legend = false, stacked = false, horizontal = false, aspectRatio = 2 } = {}) => {
  const cat = { stacked, grid: { display: false }, ticks: { font: { size: 12 }, autoSkip: false, maxRotation: horizontal ? 0 : 45, minRotation: horizontal ? 0 : 45 } };
  const val = { stacked, beginAtZero: true, grid: { color: "#E2E8F0" }, ticks: { font: { size: 12 }, precision: 0 } };
  return {
    indexAxis: horizontal ? "y" : "x",
    responsive: true,
    maintainAspectRatio: false,
    aspectRatio,
    plugins: {
      legend: {
        display: true,
        position: "bottom",
        labels: { usePointStyle: false, boxWidth: 30, boxHeight: 8, font: { size: 12 } },
      },
      tooltip: {
        enabled: true,
        callbacks: {
          label(ctx) {
            const numVal = horizontal ? ctx.parsed.x : ctx.parsed.y;
            return `${ctx.dataset.label}: ${globalNumDecFixed(numVal)} คน`;
          },
        },
      },
    },
    scales: horizontal ? { x: val, y: cat } : { x: cat, y: val },
  };
};

const ageTabs = [
  { label: "เด็ก 0-12 ปี", value: "child" },
  { label: "วัยเรียน (อายุ 3–24 ปี)", value: "student" },
  { label: "วัยทำงาน (อายุ 15–59 ปี)", value: "working" },
  { label: "ผู้สูงอายุ (อายุ 60 ปีขึ้นไป)", value: "elder" },
];
const ageTab = ref("child");
const totalPopulation = ref(0);
const femaleCount = ref(0);
const maleCount = ref(0);
const otherCount = ref(0);
const agePanelData = ref({ child: {}, student: {}, working: {}, elder: {} });
const idCardData = ref({ labels: [], datasets: [] });
const maritalData = ref({ labels: [], datasets: [] });
const ageRangeData = ref({ labels: [], datasets: [] });

const AGE_RANGE_GENDER_PALETTE = { female: "#E0A64A", male: "#345E7E", other: "#6B7280" };
const ID_CARD_GENDER_PALETTE = { female: "#E2687A", male: "#2CA6BD", other: "#8B6BC2" };
const MARITAL_GENDER_PALETTE = { female: "#D97D3D", male: "#5468BE", other: "#2FAE7E" };
const genderPair = (female, male, other, palette) => [
  bar("เพศหญิง", palette.female, female, { barPercentage: 0.8, categoryPercentage: 0.7 }),
  bar("เพศชาย", palette.male, male, { barPercentage: 0.8, categoryPercentage: 0.7 }),
  bar("อื่นๆ", palette.other, other, { barPercentage: 0.8, categoryPercentage: 0.7 }),
];

const toNumber = (value) => Number(value || 0);
const buildGenderSeries = (items, gender, hasNid) => {
  const selected = items.filter((item) => item.gender === gender && item.has_NID === hasNid);
  return toNumber(selected[0]?.total || 0);
};

const formatPercent = (num, denom) => {
  const raw = (Number(num) / Number(denom)) * 100 || 0;
  return `${Number.isInteger(raw) ? raw : raw.toFixed(1)}%`;
};

const femalePercentage = computed(() => formatPercent(femaleCount.value, totalPopulation.value));
const malePercentage = computed(() => formatPercent(maleCount.value, totalPopulation.value));
const otherPercentage = computed(() => formatPercent(otherCount.value, totalPopulation.value));

const fetchPersonOverview = async () => {
    const res = await apiGet(`member/info/dashboard/summary`);
    if (res?.status === 200) {
      const data = res.data || {};
      const byGender = Array.isArray(data.by_gender) ? data.by_gender : [];
      const nidByGender = Array.isArray(data.NID_by_gender) ? data.NID_by_gender : [];
      const maritalByGender = Array.isArray(data.marital_status_by_gender) ? data.marital_status_by_gender : [];
      const ageByGender = Array.isArray(data.age_group_by_gender) ? data.age_group_by_gender : [];

      totalPopulation.value = toNumber(data.total || byGender.reduce((sum, item) => sum + toNumber(item.total), 0));
      femaleCount.value = toNumber(byGender.find((item) => item.gender === "หญิง")?.total || 0);
      maleCount.value = toNumber(byGender.find((item) => item.gender === "ชาย")?.total || 0);
      otherCount.value = toNumber(byGender.find((item) => item.gender === "อื่นๆ")?.total || 0);

      const idCardLabels = ["มีบัตรประชาชน", "ไม่มีบัตรประชาชน"];
      const femaleNid = [buildGenderSeries(nidByGender, "หญิง", true), buildGenderSeries(nidByGender, "หญิง", false)];
      const maleNid = [buildGenderSeries(nidByGender, "ชาย", true), buildGenderSeries(nidByGender, "ชาย", false)];
      const otherNid = [buildGenderSeries(nidByGender, "อื่นๆ", true), buildGenderSeries(nidByGender, "อื่นๆ", false)];
      idCardData.value = {
        labels: idCardLabels,
        datasets: genderPair(femaleNid, maleNid, otherNid, ID_CARD_GENDER_PALETTE),
      };

      const maritalDisplay = [
        { key: "married_registered", display: ["แต่งงานและ", "จดทะเบียนสมรส"] },
        { key: "single", display: ["โสด"] },
        { key: "cohabiting", display: ["แต่งงาน/อาศัยอยู่ด้วยกัน", "แต่ไม่ได้จดทะเบียนสมรส"] },
        { key: "divorced", display: ["เลิก/หย่าร้าง"] },
        { key: "widowed", display: ["คู่สมรสเสียชีวิต"] },
        { key: "other", display: ["อื่นๆ"] },
      ];
      
      const maritalStatusMap = {
        "แต่งงานและจดทะเบียนสมรส": "married_registered",
        "โสด": "single",
        "แต่งงาน/อาศัยอยู่ด้วยกันแต่ไม่ได้จดทะเบียนสมรส": "cohabiting",
        "เลิก/หย่าร้าง": "divorced",
        "เลิกรา/หย่าร้าง": "divorced",
        "คู่สมรสเสียชีวิต": "widowed",
        "อื่น ๆ": "other",
        "อื่นๆ": "other",
      };
      
      const maritalLabels = maritalDisplay.map((item) => item.display);
      const maritalFemale = maritalDisplay.map((d) =>
        toNumber(
          maritalByGender.find(
            (item) => item.gender === "หญิง" && maritalStatusMap[(item.marital_status || "")] === d.key
          )?.total || 0
        )
      );
      const maritalMale = maritalDisplay.map((d) =>
        toNumber(
          maritalByGender.find(
            (item) => item.gender === "ชาย" && maritalStatusMap[(item.marital_status || "")] === d.key
          )?.total || 0
        )
      );
      const maritalOther = maritalDisplay.map((d) =>
        toNumber(
          maritalByGender.find(
            (item) => item.gender === "อื่นๆ" && maritalStatusMap[(item.marital_status || "")] === d.key
          )?.total || 0
        )
      );
      maritalData.value = {
        labels: maritalLabels,
        datasets: genderPair(maritalFemale, maritalMale, maritalOther, MARITAL_GENDER_PALETTE),
      };

      const ageOrder = ["ปฐมวัย", "วัยเรียน", "วัยผู้ใหญ่", "ผู้สูงอายุ"];
      const ageFemale = ageOrder.map((label) => toNumber(ageByGender.find((item) => item.gender === "หญิง" && (item.age_group || "") === label)?.total || 0));
      const ageMale = ageOrder.map((label) => toNumber(ageByGender.find((item) => item.gender === "ชาย" && (item.age_group || "") === label)?.total || 0));
      const ageOther = ageOrder.map((label) => toNumber(ageByGender.find((item) => item.gender === "อื่นๆ" && (item.age_group || "") === label)?.total || 0));
      ageRangeData.value = {
        labels: ageOrder,
        datasets: genderPair(ageFemale, ageMale, ageOther, AGE_RANGE_GENDER_PALETTE),
      };
    }
};

const ageRangeParam = computed(() => {
  switch (ageTab.value) {
    case "student": return "3-24";
    case "working": return "15-59";
    case "elder": return "60plus";
    case "child":
    default: return "0-12";
  }
});

const fetchAgePanelData = async () => {
  const res = await apiGet(`member/info/dashboard/ageGroupStats`, { age_range: ageRangeParam.value });
  if (res?.status === 200) {
    const data = res.data || {};

    const panelKey = ageTab.value === "student" ? "student" : ageTab.value === "working" ? "working" : ageTab.value === "elder" ? "elder" : "child";
    agePanelData.value = {
      ...agePanelData.value,
      [panelKey]: data,
    };
  }
};

onMounted(() => {
  fetchPersonOverview();
  fetchAgePanelData();
});
watch(filterParams, () => {
  fetchPersonOverview();
  fetchAgePanelData();
}, { deep: true });
watch(ageTab, () => {
  fetchAgePanelData();
});

const genderOptions = barOptions({ legend: true, aspectRatio: 1.1 });
</script>
